import { AppProps } from "next/app";
import Head from "next/head";
import "./global.css";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ErrorReportProvider } from "@/components/ErrorReportProvider";
import { Toaster } from "@/components/ui/toaster";
import { SWRConfig } from "swr";
import { fetcher, isProd } from "@/lib/utils";
import { ThemeProvider } from "next-themes";
import Script from "next/script";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { swrLogger } from "@/components/swrLogger";
import { NuqsAdapter } from "nuqs/adapters/next/pages";
import { AnalyticsConsent } from "@/components/analytics/AnalyticsConsent";
import { GoogleTagManager } from "@/components/analytics/GoogleTagManager";
import { useSendAnalyticsEvent } from "@/components/analytics/GoogleTagManager";
import { Inter, Instrument_Serif, JetBrains_Mono } from "next/font/google";
import { getSeo } from "@/components/useSeo";
import { SessionProvider } from "@/components/useSession";

const inter = Inter({ subsets: ["latin", "cyrillic"], variable: "--font-inter" });
const instrumentSerif = Instrument_Serif({
  weight: ["400"],
  style: ["normal", "italic"],
  subsets: ["latin"],
  variable: "--font-instrument-serif"
});
const jetbrainsMono = JetBrains_Mono({
  subsets: ["latin", "cyrillic"],
  variable: "--font-jetbrains-mono"
});

export default function App({ Component, pageProps, router }: AppProps) {
  const sendEvent = useSendAnalyticsEvent();
  const seo = getSeo(router.pathname);
  const baseUrl = process.env.NEXT_PUBLIC_APP_URL ?? "https://dollardeploy.com";
  const canonicalPath = (router.asPath ?? "/").split(/[?#]/)[0];
  const canonicalUrl = new URL(canonicalPath, baseUrl).toString();
  const imageUrl = new URL(seo.image, baseUrl).toString();
  const noIndexPaths = [
    "/dashboard",
    "/app",
    "/host",
    "/task",
    "/journal",
    "/backup",
    "/settings",
    "/auth"
  ];
  const shouldNoIndex = noIndexPaths.some(path => router.pathname.startsWith(path));
  return (
    <main
      className={`${inter.className} ${instrumentSerif.variable} ${jetbrainsMono.variable} font-sans`}
    >
      {/* https://nextjs.org/docs/pages/api-reference/components/head */}
      <Head>
        <title>{`${seo.title} | ${seo.name}`}</title>
        <link rel="icon" href="/icon.png" />
        <link rel="shortcut icon" type="image/png" href="/icon.png" />
        <link rel="apple-touch-icon" type="image/png" href="/icon.png" />
        <meta name="mobile-web-app-capable" content="yes" />
        <link rel="canonical" href={canonicalUrl} />
        <meta name="author" content="Wizecore Oy" />
        <meta name="theme-color" content="#2c1e48" />
        <meta name="robots" content={shouldNoIndex ? "noindex, nofollow" : "index, follow"} />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content={seo.title} />
        <meta property="twitter:description" content={seo.description} />
        <meta property="og:site_name" content="DollarDeploy" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:locale" content="en-GB" />
        <meta property="og:type" content="website" />
        <meta name="og:title" content={seo.title} key="title" />
        <meta name="og:image" content={imageUrl} key="image" />
        <meta name="twitter:image" content={imageUrl} key="twitter:image" />
        <meta name="og:image:alt" content={`${seo.title} cover`} />
        <meta name="description" key="description" content={seo.description} />
        <meta property="og:description" key="og:description" content={seo.description} />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1, minimum-scale=1.0"
        />
      </Head>

      {/* https://plausible.io/docs/custom-event-goals */}
      {process.env.NEXT_PUBLIC_APP_URL &&
        isProd() &&
        process.env.NEXT_PUBLIC_PLAUSIBLE === "1" && (
          <Script
            async
            defer
            data-domain={new URL(process.env.NEXT_PUBLIC_APP_URL).hostname}
            src="https://c.wizecore.com/js/collecte.js"
          />
        )}

      <SWRConfig
        value={{
          fetcher,
          use: process.env.NEXT_PUBLIC_LOG_VERBOSE === "1" ? [swrLogger] : []
        }}
      >
        <ThemeProvider
          attribute="class"
          defaultTheme="system"
          enableSystem
          disableTransitionOnChange
        >
          <Toaster />
          <ErrorBoundary>
            <ErrorReportProvider>
              <SessionProvider>
                <NuqsAdapter>
                  <TooltipProvider>
                    {process.env.NEXT_PUBLIC_ANALYTICS_CONSENT === "1" && (
                      <AnalyticsConsent
                        variant="small"
                        onConsent={consent => {
                          if (consent instanceof Date) {
                            sendEvent("consent", "update", {
                              ad_storage: "granted",
                              ad_user_data: "granted",
                              ad_personalization: "granted",
                              analytics_storage: "granted"
                            });
                          } else {
                            sendEvent("consent", "update", {
                              ad_storage: "denied",
                              ad_user_data: "denied",
                              ad_personalization: "denied",
                              analytics_storage: "denied"
                            });
                          }
                        }}
                      />
                    )}
                    <GoogleTagManager />
                    <Component {...pageProps} />
                  </TooltipProvider>
                </NuqsAdapter>
              </SessionProvider>
            </ErrorReportProvider>
          </ErrorBoundary>
        </ThemeProvider>
      </SWRConfig>
    </main>
  );
}
